import * as React from "react"
import {ChangeEvent, CSSProperties} from "react";
import {max, min, range} from "../utils";

type Alignment = "left" | "center" | "right"
export const Alignment = { left: "left" as Alignment, center: "center" as Alignment, right: "right" as Alignment }
export type Data = NodeData | (() => void)
export type NodeData = {
	special?: string
	node: React.ReactNode
	alignment?: Alignment // Defaults to "center" if not provided
	minWidth?: string | number
}

type Map<Key extends string, Value> = { [K in Key]: Value }

export type Row<TFieldName extends string> = Map<TFieldName, Data>

type StringDic = {[field: string]: string}

export interface Props<Fields extends StringDic> {
	heading: Fields
	headingAlignments?: {[K in keyof Fields]: "left"|"centre"|"right"}
	data: Row<keyof Fields>[]
	footer?: React.ReactNode
	highlight?: (r: Row<keyof Fields>) => boolean
	highlightColor?: string
	rowClick?: keyof Fields
	widths?: "leave undefined"|"set to something defaulty" // "set to something defaulty" is the default
}

export class FixedDataGrid<Fields extends StringDic> extends React.Component<Props<Fields>, {}> {
	render() {
		const fields = Object.keys(this.props.heading).filter(k => this.props.rowClick !== k)
		const makeRow = (row: Row<keyof Fields>, index: number) => {
			const customStyles: CSSProperties = {}
			const customProps: React.DetailedHTMLProps<
				React.TdHTMLAttributes<HTMLTableDataCellElement>,
				HTMLTableDataCellElement
			> = {}

			if(this.props.highlight !== undefined && this.props.highlight(row)) {
				customStyles['backgroundColor'] = this.props.highlightColor !== undefined
					? this.props.highlightColor
					: '#b5d6ed'
			}

			const rowClick = this.props.rowClick
			if(rowClick && row[rowClick] && typeof row[rowClick] == 'function') {
				customStyles['cursor'] = 'pointer'
				customProps['onClick'] = (row[rowClick] as () => void)
			}

			return <tr className={index % 2 == 0 ? "row0" : "row1"} key={`row${index}`}>
				{fields.map((field: keyof Fields) => {
					if(field == this.props.rowClick) return undefined
					const fieldData = row[field]
					if(typeof fieldData == 'function') return undefined
					const data: NodeData = fieldData as NodeData // we have exhausted the alternatives
					const alignment: Alignment = data.alignment || "center"
					return <td
						{...customProps}
						style={{
							...customStyles,
							textAlign: alignment,
							minWidth: data.minWidth
						}}
						key={`field${field}row${index}`}
					>
						{data != null ? data.node : null}
					</td>
				})}
			</tr>
		}

		const width = this.props.widths == "leave undefined" ? undefined : "50px"

		const headings = fields.map((field) =>
			<th style={{width, textAlign: this.props.headingAlignments ? this.props.headingAlignments[field] : "center"}} key={`heading-${field}`}>
				{this.props.heading[field]}
			</th>
		)

		const rows = this.props.data.map(makeRow)

		return <div className="dataGridPanel sbRoundedEdges" style={{width: "100%"}}>
			<table className="dataGrid sbRoundedEdges tablesorter" cellSpacing="0" cellPadding="2" style={{width: "100%", visibility: "visible"}}>
				<thead>
				<tr className="dataGridTitlebar sbRoundedEdges" style={{height: "30px"}}>
					{headings}
				</tr>
				</thead>
				<tbody>
					{rows}
				</tbody>
				{this.props.footer && <tfoot>{this.props.footer}</tfoot>}
			</table>
		</div>
	}
}

export type PageValueProps<Label extends string> = {
	limit: number|null

	//note: the ``page'' is independent of the limit; it's simply some row that should be visible.
	page: number

	label: Label
}
export type PageCallbackProps = {
	setLimit: (limit: number|null) => void

	//note: the ``page'' is independent of the limit; it's simply some row that should be visible.
	setPage: (page: number) => void
}

export type PageProps = PageValueProps<any> & PageCallbackProps

const makeLimits = (limit: number|null, length: number) => {
	const limits = [10, 20, 50, 100]
	if(limit && limits.indexOf(limit) < 0) {
		limits.push(limit)
	}
	return limits.filter(l => l < length)
}

const findFirstRow = (limit: number, rowToShow: number, length: number) => {
	// we must start at at least the first row
	rowToShow = max(0, rowToShow)
	console.log(rowToShow)

	//we can't have more pages than are available
	rowToShow = min(rowToShow, length)
	console.log(rowToShow)

	//we want to start at an integral number of pages considering the length
	let page = Math.ceil( rowToShow/limit)
	console.log(page)

	//we're trying to find the first row. Why one less the ceiling? 10/10 = 1, but we need to start at 0.
	page = page - 1
	console.log(page)

	return page * limit
}

export class PaginatedDataGrid<Fields extends StringDic> extends React.Component<PageProps & Props<Fields>, {}> {
	render() {
		const props = this.props
		const numRowsTotal = props.data.length
		const numColumns = Object.keys(props.heading).length
		const hasLimit = props.limit !== null && numRowsTotal > 0

		const limits = makeLimits(props.limit, numRowsTotal)
		const pages = hasLimit ? range(1, props.limit ? Math.ceil(numRowsTotal / props.limit) : 1) : []

		const onChangeLimit = (e: ChangeEvent<HTMLSelectElement>) => {
			const newLimit = Number.parseInt(e.currentTarget.value)
			props.setLimit(isNaN(newLimit) ? null : newLimit)
		}

		const onChangePage = (e: ChangeEvent<HTMLSelectElement>) => {
			const newPage = Number.parseInt(e.currentTarget.value)
			props.setPage(isNaN(newPage) || !props.limit ? 1 : (newPage - 1) * props.limit + 1)
		}

		const footer = <tr>
			<td style={{textAlign: "center"}} colSpan={max(numColumns / (hasLimit ? 2 : 1), 1)}>
				Show rows: <select onChange={onChangeLimit}>
				{limits.map(l => <option value={l} key={l}>{l}</option>)}
				<option value="all">All</option>
			</select>
			</td>
			{hasLimit &&
			<td style={{textAlign: "center"}} colSpan={max(numColumns / 2, 1)}>
				Show page: <select onChange={onChangePage}>
				{pages.map(p => <option value={p} key={p}>{p}</option>)}
			</select>
			</td>
			}
		</tr>

		const sliced = props.limit
			? ((offset) => props.data.slice(offset, offset + props.limit))(findFirstRow(props.limit, props.page, numRowsTotal))
			: props.data

		return <div>
			<FixedDataGrid
				heading={props.heading}
				data={sliced}
				footer={footer}
			/>
		</div>
	}
}






// WEBPACK FOOTER //
// ./src/lib/sbhtml/fixed-data-grid.tsx